import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

import { diskussionService } from '../../services/diskussionService';
import type {
  DiskussionChecklist as DiskussionChecklistData,
  DiskussionChecklistItem,
} from '../../types';

export interface DiskussionChecklistProps {
  postId: number;
  /** Wenn true, sind Schreibaktionen ausgeblendet (z. B. gesperrter Thread fuer Nicht-Mods). */
  readOnly?: boolean;
}

function formatDoneTooltip(item: DiskussionChecklistItem): string {
  if (item.done_by.length === 0) return 'Noch niemand hat abgehakt';
  const list = item.done_by
    .map((d) => {
      const ts = new Date(d.done_at);
      const tsStr = isNaN(ts.getTime())
        ? d.done_at
        : ts.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
      return `${d.name} (${tsStr})`;
    })
    .join('\n');
  return `Erledigt von:\n${list}`;
}

interface ParsedChecklistInputItem {
  text: string;
  done: boolean;
}

const CHECKBOX_LINE_RE = /^(\s*)[-*]\s+\[([ xX])\]\s*(.*)$/;

function stripTodoMarker(text: string): string {
  return text.replace(/(^|\s)@TODO\b\s*/gi, '$1').trim();
}

function indentationDepth(indent: string): number {
  const columns = Array.from(indent).reduce((sum, ch) => sum + (ch === '\t' ? 2 : 1), 0);
  return Math.floor(columns / 2);
}

function splitDisplayIndent(text: string): { indentLevel: number; label: string } {
  const match = text.match(/^(\s*)(.*)$/s);
  const indent = match?.[1] ?? '';
  return {
    indentLevel: indentationDepth(indent),
    label: match?.[2] ?? text,
  };
}

function parseChecklistInput(input: string): ParsedChecklistInputItem[] {
  return input
    .split(/\r?\n/)
    .map((line) => {
      const match = line.match(CHECKBOX_LINE_RE);
      if (!match) return null;
      const text = stripTodoMarker(match[3] ?? '');
      if (!text) return null;
      return {
        text: `${'  '.repeat(indentationDepth(match[1] ?? ''))}${text}`,
        done: match[2].toLowerCase() === 'x',
      };
    })
    .filter((item): item is ParsedChecklistInputItem => item !== null);
}

const DiskussionChecklist: React.FC<DiskussionChecklistProps> = ({ postId, readOnly = false }) => {
  const [data, setData] = useState<DiskussionChecklistData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newItemText, setNewItemText] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [hideDone, setHideDone] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<number>>(() => new Set());

  const setItemBusy = useCallback((itemId: number, busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await diskussionService.getChecklist(postId);
      setData(d);
    } catch (err) {
      console.error('Checklist laden fehlgeschlagen:', err);
      setError('Checkliste konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const replaceItem = useCallback((updated: DiskussionChecklistItem) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((it) => (it.id === updated.id ? updated : it)),
      };
    });
  }, []);

  const handleToggleDone = useCallback(
    async (item: DiskussionChecklistItem) => {
      if (readOnly) return;
      const previous = item;
      const optimistic: DiskussionChecklistItem = {
        ...item,
        done_by_me: !item.done_by_me,
        done_count: item.done_by_me ? item.done_count - 1 : item.done_count + 1,
      };
      replaceItem(optimistic);
      setItemBusy(item.id, true);
      try {
        const updated = previous.done_by_me
          ? await diskussionService.unmarkChecklistItemDone(postId, item.id)
          : await diskussionService.markChecklistItemDone(postId, item.id);
        replaceItem(updated);
      } catch (err) {
        console.error('Erledigt-Toggle fehlgeschlagen:', err);
        replaceItem(previous);
      } finally {
        setItemBusy(item.id, false);
      }
    },
    [postId, readOnly, replaceItem, setItemBusy],
  );

  const handleAddItem = useCallback(async () => {
    const text = newItemText.trim();
    if (!text || readOnly) return;
    const parsedItems = parseChecklistInput(newItemText);
    const itemsToCreate = parsedItems.length > 0
      ? parsedItems
      : [{ text: stripTodoMarker(text), done: false }];
    if (itemsToCreate.length === 0 || itemsToCreate.every((item) => item.text.length === 0)) return;
    setAdding(true);
    try {
      const createdItems: DiskussionChecklistItem[] = [];
      for (const item of itemsToCreate) {
        const created = await diskussionService.addChecklistItem(postId, item.text);
        const finalItem = item.done
          ? await diskussionService.markChecklistItemDone(postId, created.id)
          : created;
        createdItems.push(finalItem);
      }
      setData((prev) => (prev ? { ...prev, items: [...prev.items, ...createdItems] } : prev));
      setNewItemText('');
    } catch (err) {
      console.error('Item(s) hinzufuegen fehlgeschlagen:', err);
      reload();
    } finally {
      setAdding(false);
    }
  }, [newItemText, postId, readOnly, reload]);

  const startEdit = useCallback((item: DiskussionChecklistItem) => {
    setEditingId(item.id);
    setEditingText(item.text);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingText('');
  }, []);

  const saveEdit = useCallback(async () => {
    if (editingId == null) return;
    const text = editingText.replace(/\s+$/g, '');
    if (!text.trim()) {
      cancelEdit();
      return;
    }
    setItemBusy(editingId, true);
    try {
      const updated = await diskussionService.updateChecklistItem(postId, editingId, { text });
      replaceItem(updated);
      setEditingId(null);
      setEditingText('');
    } catch (err) {
      console.error('Item bearbeiten fehlgeschlagen:', err);
    } finally {
      setItemBusy(editingId, false);
    }
  }, [editingId, editingText, postId, replaceItem, setItemBusy, cancelEdit]);

  const handleDelete = useCallback(
    async (item: DiskussionChecklistItem) => {
      if (readOnly) return;
      if (!window.confirm(`Item „${item.text}" wirklich entfernen?`)) return;
      setItemBusy(item.id, true);
      try {
        await diskussionService.deleteChecklistItem(postId, item.id);
        setData((prev) =>
          prev ? { ...prev, items: prev.items.filter((i) => i.id !== item.id) } : prev,
        );
      } catch (err) {
        console.error('Item loeschen fehlgeschlagen:', err);
      } finally {
        setItemBusy(item.id, false);
      }
    },
    [postId, readOnly, setItemBusy],
  );

  const totalDone = useMemo(
    () => (data?.items ?? []).filter((i) => i.done_count > 0).length,
    [data],
  );
  const totalItems = data?.items.length ?? 0;
  const visibleItems = useMemo(
    () => (data?.items ?? []).filter((item) => !hideDone || item.done_count === 0),
    [data, hideDone],
  );
  const hiddenDoneCount = totalItems - visibleItems.length;

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Checkliste laden …
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5, my: 1 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Paper>
    );
  }

  if (!data) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        my: 1,
        borderColor: (t) => t.palette.primary.main,
        bgcolor: 'background.paper',
      }}
      data-aio-checklist-post-id={postId}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <CheckBoxIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Checkliste
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {totalDone}/{totalItems} erledigt
        </Typography>
        {totalDone > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={() => setHideDone((v) => !v)}
            sx={{ ml: 'auto', minWidth: 0 }}
          >
            {hideDone ? 'Erledigte anzeigen' : 'Erledigte ausblenden'}
          </Button>
        )}
      </Stack>

      {data.items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
          Noch keine Aufgaben. {readOnly ? '' : 'Fügen Sie unten den ersten Eintrag hinzu.'}
        </Typography>
      )}

      {hideDone && hiddenDoneCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {hiddenDoneCount} erledigte {hiddenDoneCount === 1 ? 'Aufgabe' : 'Aufgaben'} ausgeblendet.
        </Typography>
      )}

      <Stack spacing={0.5}>
        {visibleItems.map((item) => {
          const isEditing = editingId === item.id;
          const busy = busyIds.has(item.id);
          const { indentLevel, label } = splitDisplayIndent(item.text);
          const doneByAnyone = item.done_count > 0;
          return (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                py: 0.25,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Tooltip title={readOnly ? '' : 'Für mich erledigt / nicht erledigt'} placement="top">
                <span>
                  <Checkbox
                    size="small"
                    checked={item.done_by_me}
                    disabled={readOnly || busy}
                    onChange={() => handleToggleDone(item)}
                    sx={{ p: 0.5 }}
                  />
                </span>
              </Tooltip>
              <Box sx={{ flex: 1, minWidth: 0, pl: isEditing ? 0 : indentLevel * 2 }}>
                {isEditing ? (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      sx={{ flex: 1, minWidth: 0 }}
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          saveEdit();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                    />
                    <IconButton size="small" onClick={saveEdit} disabled={busy} aria-label="Speichern">
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={cancelEdit} aria-label="Abbrechen">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ) : (
                  <Tooltip
                    title={formatDoneTooltip(item)}
                    placement="top-start"
                    enterDelay={400}
                  >
                    <Typography
                      variant="body2"
                      onDoubleClick={() => !readOnly && startEdit(item)}
                      sx={{
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        textDecoration: doneByAnyone ? 'line-through' : 'none',
                        color: doneByAnyone ? 'text.secondary' : 'text.primary',
                        cursor: readOnly ? 'default' : 'text',
                        py: 0.5,
                      }}
                    >
                      {label}
                      {item.done_count > 0 && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          ({item.done_count}× erledigt)
                        </Typography>
                      )}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
              {!readOnly && !isEditing && (
                <Stack direction="row" spacing={0}>
                  <Tooltip title="Bearbeiten">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => startEdit(item)}
                        disabled={busy}
                        aria-label="Bearbeiten"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Entfernen">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item)}
                        disabled={busy}
                        aria-label="Entfernen"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>

      {!readOnly && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          sx={{ mt: 1, minWidth: 0 }}
        >
          <TextField
            size="small"
            fullWidth
            multiline
            sx={{ flex: 1, minWidth: 0 }}
            minRows={1}
            maxRows={8}
            placeholder="Neuen Eintrag oder Markdown-Checkboxliste einfügen …"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleAddItem();
              }
            }}
            disabled={adding}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            disabled={adding || newItemText.trim().length === 0}
            sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            {parseChecklistInput(newItemText).length > 1 ? 'Importieren' : 'Hinzufügen'}
          </Button>
        </Stack>
      )}
    </Paper>
  );
};

export default DiskussionChecklist;
